import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { expenseAPI } from '../../api/expenses';
import { Expense } from '../../types/entities';
import { Plus, Trash2, Search, Edit, Eye } from 'lucide-react';
import { ReceiptButton } from '../../components/Documents';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { DateRangeQuickFilter } from '../../components/UI/DateRangeQuickFilter';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

export const ExpenseList: React.FC = () => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    type: '',
  });
  const navigate = useNavigate();

  const getExpenseTypeLabel = (type: string) => t(`expenses.types.${type}`, { defaultValue: type });

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    try {
      const data = await expenseAPI.getAll({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        type: filters.type || undefined,
      });
      setExpenses(data);
    } catch (error) {
      toast.error(t('expenses.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    loadExpenses();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('expenses.deleteConfirm'))) return;

    try {
      await expenseAPI.delete(id);
      toast.success(t('expenses.deleteSuccess'));
      loadExpenses();
    } catch (error) {
      toast.error(t('expenses.deleteError'));
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => {
    const amount = typeof e.amount === 'number' ? e.amount : 0;
    return sum + amount;
  }, 0);

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('expenses.title')}</h1>
        <button
          onClick={() => navigate('/expenses/new')}
          className="btn-primary flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('expenses.addNew')}
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-end space-x-4">
          <DateRangeQuickFilter
            startDate={filters.startDate}
            endDate={filters.endDate}
            onStartDateChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
            onEndDateChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
          />
          <div>
            <label className="block text-sm font-medium mb-1">{t('expenses.type')}</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="input-field"
            >
              <option value="">{t('common.all')}</option>
              <option value="FUEL">{t('expenses.types.FUEL')}</option>
              <option value="REPAIR">{t('expenses.types.REPAIR')}</option>
              <option value="OTHER">{t('expenses.types.OTHER')}</option>
            </select>
          </div>
          <button onClick={handleSearch} className="btn-primary flex items-center">
            <Search className="w-4 h-4 mr-2" />
            {t('common.search')}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="card bg-primary-50">
        <div className="flex justify-between items-center">
          <span className="text-gray-700">{t('reports.totalExpenses')}:</span>
          <span className="text-2xl font-bold text-primary-600">
            {formatCurrency(totalExpenses)} đ
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3 table-header">{t('expenses.date')}</th>
              <th className="pb-3 table-header">{t('expenses.type')}</th>
              <th className="pb-3 table-header">{t('expenses.vehicle')}</th>
              <th className="pb-3 table-header">{t('expenses.trip')}</th>
              <th className="pb-3 table-header">{t('expenses.description')}</th>
              <th className="pb-3 table-header text-right">{t('expenses.amount')}</th>
              <th className="pb-3 table-header">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-gray-500">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              expenses.map((expense) => (
                <tr key={expense.id} className="border-b last:border-0">
                  <td className="py-3">
                    {format(new Date(expense.date), 'dd/MM/yyyy')}
                  </td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-1 rounded text-sm ${
                        expense.type === 'FUEL'
                          ? 'bg-blue-100 text-blue-700'
                          : expense.type === 'REPAIR'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {getExpenseTypeLabel(expense.type)}
                    </span>
                  </td>
                  <td className="py-3">{expense.vehicle?.licensePlate || '-'}</td>
                  <td className="py-3">
                    {expense.tripId ? `#${expense.tripId}` : '-'}
                  </td>
                  <td className="py-3 text-gray-600">
                    {expense.type === 'FUEL'
                      ? `${expense.quantity} L x ${formatCurrency(expense.unitPrice || 0)}`
                      : expense.description || '-'}
                  </td>
                  <td className="py-3 text-center font-medium">
                    {formatCurrency(expense.amount)} đ
                  </td>
                  <td className="py-3">
                    <div className="flex items-center space-x-1">
                      <ReceiptButton type="expense" entityId={expense.id} className="text-xs" />
                      <button
                        onClick={() => navigate(`/expenses/${expense.id}/edit`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('common.edit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
